














































































import { Vue, Component, Prop, Emit } from "vue-property-decorator";
import Link from "./Link.vue";
import { Clinic, Range24Hr, Weekdays } from "@/model/Boroughs";
import { StringMap } from "@/model/Model";
import { removeWidows } from "@/utils";

interface DayTimeRange {
  day: string;
  timeRange: Range24Hr;
}

@Component({
  components: {
    Link
  }
})
export default class ServiceInformation extends Vue {
  @Prop() public clinic!: Clinic | null;
  @Prop() public selected!: boolean;

  private padLeft(num: number, len: number = 4): string {
    const numString = num.toString();
    if (numString.length < len) {
      const zeros = new Array(len - numString.length + 1).join("0");
      return `${zeros}${numString}`;
    }
    return numString;
  }

  @Emit("navigate")
  private gotoSupportFinder(event: Event) {
    event.preventDefault();
    return "find-support";
  }

  private formatWebsite(website: string): string {
    website = website.replace("https://", "").replace("http://", "");
    // Truncate long website addresses.
    if (website.length > 40) {
      return website.slice(0, website.indexOf("/") + 1) + "...";
    }
    return website;
  }

  private formatAddress(address: string): string {
    return removeWidows(address, 3);
  }

  private formatPhone(
    phone: string | { title: string; value: string }
  ): string {
    let p: string;
    if (typeof phone === "string") {
      p = phone;
    } else {
      p = phone.value;
    }
    return p.replace(/-/g, " ");
  }

  private formatTimes(weekdays: Weekdays, format: boolean): string {

    // Aggregate weekday opening times into something nice and readable.
    const getDayNum = (day: string): number => {
      switch (day) {
        case "monday":
          return 0;
        case "tuesday":
          return 1;
        case "wednesday":
          return 2;
        case "thursday":
          return 3;
        case "friday":
          return 4;
        case "saturday":
          return 5;
        case "sunday":
          return 6;
        default:
          return 0;
      }
    };

    const capitalize = (str: string): string => {
      return str[0].toUpperCase() + str.slice(1);
    };

    const abbreviateDay = (day: string): string => {
      switch (day) {
        case "monday":
          return "mon";
        case "tuesday":
          return "tues";
        case "wednesday":
          return "wed";
        case "thursday":
          return "thurs";
        case "friday":
          return "fri";
        case "saturday":
          return "sat";
        case "sunday":
          return "sun";
        default:
          return "mon";
      }
    };

    const formatTime24 = (num: number): { time: string; suffix: string } => {
      const timeString = this.padLeft(num, 4);
      let hour = parseInt(timeString.substr(0, 2), 10);
      const mins = timeString.substr(2, 2);
      let pm = false;
      if (hour > 11) {
        pm = true;
      }
      if (hour >= 13) {
        hour -= 12;
      }
      return {
        time: mins !== "00" ? `${hour}:${mins}` : `${hour}`,
        suffix: hour === 12 ? "noon" : pm ? "pm" : "am"
      };
    };
    let out = "";
    if (format) {
      const timesGrouped: StringMap<DayTimeRange[]> = {};
      // Group days by common times:
      for (const [key, day] of Object.entries(weekdays)) {
        for (const timeRange of day) {
          const rangeHash = `${this.padLeft(timeRange.start)}${this.padLeft(
            timeRange.end
          )}`;
          if (!timesGrouped.hasOwnProperty(rangeHash)) {
            timesGrouped[rangeHash] = [];
          }
          timesGrouped[rangeHash].push({
            day: key,
            timeRange
          });
        }
      }
      const runs: StringMap<DayTimeRange[][]> = {};
      for (const timeRangeHash of Object.keys(timesGrouped)) {
        const timeRange = timesGrouped[timeRangeHash];
        // See if there are any consecutive runs of days - more than two.
        let lastRange;

        runs[timeRangeHash] = [[]];
        let currentRunNum = runs[timeRangeHash].length - 1;
        for (const range of timeRange) {
          const dayNum = getDayNum(range.day);
          const runContinues =
            lastRange && getDayNum(lastRange.day) === dayNum - 1;
          if (runContinues || currentRunNum === 0) {
            runs[timeRangeHash][currentRunNum].push(range);
          } else if (!runContinues && currentRunNum > 0) {
            lastRange = null;
            currentRunNum = runs[timeRangeHash].length;
            runs[timeRangeHash].push([]);
            runs[timeRangeHash][currentRunNum].push(range);
          }
          lastRange = range;
        }
      }
      const sortedDays = Object.entries(runs);
      sortedDays.sort(([ka, va], [kb, vb]) => {
        if (getDayNum(va[0][0].day) === getDayNum(vb[0][0].day)) {
          return ka < kb ? -1 : 1;
        }
        return getDayNum(va[0][0].day) < getDayNum(vb[0][0].day) ? -1 : 1;
      });

      const outDays = [];
      for (const [key, subRuns] of sortedDays) {
        for (const run of subRuns) {
          // See if it's an actual run of days:
          let isRun = false;
          if (run.length > 4) {
            isRun = true;
            let prevDayNum = 0;
            for (let i = 0; i < run.length; i++) {
              const dayNum = getDayNum(run[i].day);
              if (i > 0 && dayNum > prevDayNum + 1) {
                isRun = false;
                break;
              }
              prevDayNum = dayNum;
            }
          }

          const start = run[0];
          const end = run[run.length - 1];
          const startDay = capitalize(abbreviateDay(start.day));
          const endDay = capitalize(abbreviateDay(end.day));
          const startTime = formatTime24(start.timeRange.start);
          const endTime = formatTime24(start.timeRange.end);
          let rangeString: string;
          if (startTime.suffix === endTime.suffix) {
            rangeString = `${startTime.time} &ndash; ${endTime.time}${endTime.suffix}`;
          } else {
            rangeString = `${startTime.time}${startTime.suffix} &ndash; ${endTime.time}${endTime.suffix}`;
          }
          if (run.length === 1) {
            outDays.push([start.day, `${startDay}: ${rangeString}`]);
          } else if (!isRun) {
            const dayNames = run
              .slice(0, run.length)
              .map(d => [
                d.day,
                `${capitalize(abbreviateDay(d.day))}: ${rangeString}`
              ]);
            for (const d of dayNames) {
              outDays.push(d);
            }
          } else {
            if (startDay === "Mon" && endDay === "Sun") {
              // Omit days if it's all week
              outDays.push(["monday", `7 days, ${rangeString}`]);
            } else {
              outDays.push([
                start.day,
                `${startDay} to ${endDay}: ${rangeString}`
              ]);
            }
          }
        }
      }
      outDays.sort(([ka, va], [kb, vb]) => {
        return getDayNum(ka) < getDayNum(kb) ? -1 : 1;
      });
      out += "<br>";
      out += outDays.map(([a, b]) => b).join("<br>");
    } else {
      for (const [key, day] of Object.entries(weekdays)) {
        out += `<br>${capitalize(abbreviateDay(key))}: `;
        const t = [];
        for (const timeRange of day) {
          const s = formatTime24(timeRange.start);
          const e = formatTime24(timeRange.end);
          t.push(`${s.time}${s.suffix} - ${e.time}${e.suffix}`);
        }
        out += t.join(", ");
      }
    }
    return out;
  }
}
